import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Users,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  UserCheck,
  FileText,
  TrendingUp,
  AlertCircle,
  Stethoscope,
  Bed,
  Timer,
  Star,
  RefreshCw,
  Search,
  Bell,
  ArrowRight,
  ChevronRight
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { API_BASE_URL } from '../../lib/constants';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // State for backend data
  const [dashboardData, setDashboardData] = useState({});
  const [todaysOperations, setTodaysOperations] = useState([]);
  const [surgeryTypesData, setSurgeryTypesData] = useState([]);
  const [patientFlowData, setPatientFlowData] = useState([]);
  const [otUtilizationData, setOtUtilizationData] = useState([]);
  const [otStatusData, setOtStatusData] = useState([]);
  const [weeklyOperationsData, setWeeklyOperationsData] = useState([]);
  const [delayAlerts, setDelayAlerts] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000); // Auto-refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboardMetrics(),
        fetchTodaysOperations(),
        fetchSurgeryTypes(),
        fetchPatientFlow(),
        fetchOtUtilization(),
        fetchOtStatus(),
        fetchWeeklyOperations(),
        fetchDelayAlerts()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchDashboardMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard`);
      const result = await response.json();
      if (result.status === 'success') {
        setDashboardData(result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    }
  };

  const fetchTodaysOperations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/today_operations`);
      const result = await response.json();
      if (result.status === 'success') {
        setTodaysOperations(result.data);
      }
    } catch (error) {
      console.error('Error fetching today\'s operations:', error);
    }
  };

  const fetchSurgeryTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/surgery_types_distribution`);
      const result = await response.json();
      if (result.status === 'success') {
        setSurgeryTypesData(result.data);
      }
    } catch (error) {
      console.error('Error fetching surgery types:', error);
    }
  };

  const fetchPatientFlow = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/patient_flow`);
      const result = await response.json();
      if (result.status === 'success') {
        setPatientFlowData(result.data);
      }
    } catch (error) {
      console.error('Error fetching patient flow:', error);
    }
  };

  const fetchOtUtilization = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ot_utilization`);
      const result = await response.json();
      if (result.status === 'success') {
        setOtUtilizationData(result.data);
      }
    } catch (error) {
      console.error('Error fetching OT utilization:', error);
    }
  };

  const fetchOtStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ot_status`);
      const result = await response.json();
      if (result.status === 'success') {
        setOtStatusData(result.data);
      } else {
        setOtStatusData([
          { ot: 1, status: 'Available', patient: null, end_time: null },
          { ot: 2, status: 'Occupied', patient: 'John Doe', end_time: '14:30' },
          { ot: 3, status: 'Available', patient: null, end_time: null },
          { ot: 4, status: 'Maintenance', patient: null, end_time: null }
        ]);
      }
    } catch (error) {
      console.error('Error fetching OT status:', error);
      setOtStatusData([
        { ot: 1, status: 'Available', patient: null, end_time: null },
        { ot: 2, status: 'Occupied', patient: 'John Doe', end_time: '14:30' },
        { ot: 3, status: 'Available', patient: null, end_time: null },
        { ot: 4, status: 'Maintenance', patient: null, end_time: null }
      ]);
    }
  };

  const fetchWeeklyOperations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/weekly_operations`);
      const result = await response.json();
      if (result.status === 'success') {
        setWeeklyOperationsData(result.data);
      }
    } catch (error) {
      console.error('Error fetching weekly operations:', error);
    }
  };

  const fetchDelayAlerts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/operation_delay_alerts`);
      const result = await response.json();
      if (result.status === 'success') {
        setDelayAlerts(result.data);
      }
    } catch (error) {
      console.error('Error fetching delay alerts:', error);
    }
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

  const MetricCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-white p-6 rounded-[1.5rem] shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 border border-slate-100 relative overflow-hidden group"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110 duration-500"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 rounded-2xl" style={{ backgroundColor: color + '15' }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {trend && (
            <div className="flex items-center px-2 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-black">
              <TrendingUp className="w-3 h-3 mr-1" />
              {trend}
            </div>
          )}
        </div>

        <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
        <div className="flex items-baseline space-x-2">
          <h3 className="text-3xl font-black text-slate-900 leading-none">{value || 0}</h3>
          {subtitle && <span className="text-[10px] font-bold text-slate-400 uppercase">{subtitle}</span>}
        </div>
      </div>
    </motion.div>
  );

  if (loading && !dashboardData.scheduled_operation) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mb-4"
          />
          <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Synchronizing Workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-8 font-sans transition-all duration-500">

      {/* Header Section */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 mb-12">
        <div>
          <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            <span>Workspace</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-teal-600">Operations Dashboard</span>
          </div>
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter">
            System <span className="text-teal-600">Overview</span>
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="bg-white px-6 py-4 rounded-[1.5rem] shadow-sm border border-slate-100 flex items-center space-x-6">
            <div className="flex items-center space-x-3 pr-6 border-r border-slate-100">
              <div className="p-2 bg-slate-50 rounded-xl">
                <Clock className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 capitalize">Live System Time</p>
                <p className="text-sm font-black text-slate-800 tracking-tight">
                  {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-teal-50 rounded-xl">
                <Calendar className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 capitalize">Current Date</p>
                <p className="text-sm font-black text-slate-800 tracking-tight">
                  {currentTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchAllData}
            className="p-4 bg-slate-900 text-white rounded-2xl shadow-xl shadow-slate-200 hover:bg-teal-600 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </motion.button>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex items-center space-x-1 bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100 w-fit mb-12 relative overflow-hidden">
        {['overview', 'operations', 'analytics'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`relative px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 z-10 ${activeTab === tab ? 'text-white' : 'text-slate-400 hover:text-slate-600'
              }`}
          >
            {activeTab === tab && (
              <motion.div
                layoutId="activeTabBg"
                className="absolute inset-0 bg-teal-500 rounded-xl -z-10"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
            {tab}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-12">
              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Scheduled"
                  value={dashboardData.scheduled_operation}
                  icon={Calendar}
                  color="#3B82F6"
                  trend="+2 TODAY"
                />
                <MetricCard
                  title="Available OTs"
                  value={(dashboardData.total_ots || 10) - (otStatusData.filter(ot => ot.status === 'Occupied' || ot.status === 'Maintenance').length || dashboardData.ongoing_operation || 0)}
                  icon={Bed}
                  color="#10B981"
                  subtitle={`of ${dashboardData.total_ots || 10} Rooms`}
                />
                <MetricCard
                  title="Ongoing"
                  value={dashboardData.ongoing_operation}
                  icon={Activity}
                  color="#F59E0B"
                />
                <MetricCard
                  title="Completed"
                  value={dashboardData.completed_operation}
                  icon={CheckCircle}
                  color="#8B5CF6"
                  trend="15% Efficient"
                />
              </div>

              {/* Secondary Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                <MetricCard
                  title="Total Patients"
                  value={dashboardData.total_patients}
                  icon={Users}
                  color="#EF4444"
                  subtitle="Admitted"
                />
                <MetricCard
                  title="Postponed"
                  value={dashboardData.postponed_operation}
                  icon={XCircle}
                  color="#64748b"
                />
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
                {/* Chart Box 1 */}
                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-lg font-black text-slate-800 tracking-tight">Surgery Distribution</h3>
                    <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      <span>Daily Report</span>
                      <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md">
                        <TrendingUp className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={surgeryTypesData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                        angle={-20}
                        textAnchor="end"
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)', fontSize: '12px', fontWeight: 800 }}
                        cursor={{ fill: '#f8fafc' }}
                      />
                      <Bar dataKey="value" fill="#3B82F6" radius={[6, 6, 0, 0]} barSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart Box 2 */}
                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                  <h3 className="text-lg font-black text-slate-800 tracking-tight mb-8">Operating Efficiency</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Scheduled', value: dashboardData.scheduled_operation || 0 },
                          { name: 'Ongoing', value: dashboardData.ongoing_operation || 0 },
                          { name: 'Completed', value: dashboardData.completed_operation || 0 },
                          { name: 'Postponed', value: dashboardData.postponed_operation || 0 }
                        ]}
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {[0, 1, 2, 3].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={10} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center flex-wrap gap-4 mt-4">
                    {['Scheduled', 'Ongoing', 'Completed', 'Postponed'].map((item, i) => (
                      <div key={item} className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i] }}></div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'operations' && (
            <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Today's Schedule Unified Table */}
              <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-8 border-b border-slate-50 flex items-center justify-between">
                  <h3 className="text-lg font-black text-slate-800 tracking-tight flex items-center">
                    <Clock className="w-5 h-5 mr-3 text-teal-500" />
                    Surgery Worklist
                  </h3>
                  <div className="px-3 py-1 bg-amber-50 text-amber-600 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {todaysOperations.length} Total Today
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50/50">
                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Timeline</th>
                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Patient</th>
                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Procedure</th>
                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Unit</th>
                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Surgeon</th>
                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {todaysOperations.length > 0 ? todaysOperations.map((surgery, index) => (
                        <tr key={index} className="group hover:bg-slate-50/80 transition-colors duration-300">
                          <td className="px-8 py-6 font-black text-slate-800 text-sm whitespace-nowrap">{surgery.time}</td>
                          <td className="px-8 py-6 font-bold text-slate-700 text-sm">{surgery.patient}</td>
                          <td className="px-8 py-6">
                            <span className="text-sm font-bold text-slate-500">{surgery.surgery}</span>
                          </td>
                          <td className="px-8 py-6">
                            <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-black">{surgery.ot}</span>
                          </td>
                          <td className="px-8 py-6 text-sm font-bold text-slate-600 italic">Dr. {surgery.surgeon}</td>
                          <td className="px-8 py-6">
                            <div className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${surgery.status === 'ongoing' ? 'bg-amber-100 text-amber-600' :
                                surgery.status === 'completed' ? 'bg-emerald-100 text-emerald-600' :
                                  'bg-blue-100 text-blue-600'
                              }`}>
                              <div className={`w-1.5 h-1.5 rounded-full mr-2 ${surgery.status === 'ongoing' ? 'bg-amber-500 animate-pulse' :
                                  surgery.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'
                                }`}></div>
                              {surgery.status}
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-8 py-20 text-center">
                            <div className="flex flex-col items-center">
                              <div className="p-4 bg-slate-50 rounded-full mb-4">
                                <AlertCircle className="w-8 h-8 text-slate-300" />
                              </div>
                              <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No entries scheduled</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* OT Status Grid Enhanced */}
              <div>
                <div className="flex items-center justify-between mb-8 px-2">
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">Real-time OT Monitoring</h3>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-[10px] font-black text-slate-400 uppercase">Available</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                      <span className="text-[10px] font-black text-slate-400 uppercase">Occupied</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {otStatusData.map((ot) => (
                    <motion.div
                      key={ot.ot}
                      whileHover={{ y: -5 }}
                      className={`p-6 rounded-[2rem] border-2 transition-all duration-300 relative overflow-hidden bg-white shadow-sm ${ot.status === 'Occupied' ? 'border-rose-100 shadow-rose-200/20' :
                          ot.status === 'Available' ? 'border-emerald-100 shadow-emerald-200/20' :
                            'border-slate-100 shadow-slate-200/20'
                        }`}
                    >
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Room</span>
                          <h4 className="text-2xl font-black text-slate-800 leading-none">{ot.ot}</h4>
                        </div>
                        <div className={`p-2 rounded-xl ${ot.status === 'Occupied' ? 'bg-rose-50 text-rose-600' :
                            ot.status === 'Available' ? 'bg-emerald-50 text-emerald-600' :
                              'bg-slate-50 text-slate-400'
                          }`}>
                          {ot.status === 'Occupied' ? <AlertTriangle className="w-5 h-5" /> : <Bed className="w-5 h-5" />}
                        </div>
                      </div>

                      {ot.status === 'Occupied' ? (
                        <div className="space-y-4">
                          <div className="p-4 bg-slate-50 rounded-2xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Active Patient</p>
                            <p className="text-xs font-black text-slate-900 line-clamp-1">{ot.patient || 'ER CASE-812'}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-3 h-3 text-rose-500" />
                            <span className="text-[10px] font-black text-rose-600 uppercase">ETC: {ot.end_time || '15:20'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-8 flex flex-col items-center">
                          <div className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full ${ot.status === 'Available' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'
                            }`}>
                            Ready for Intake
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-10 pb-12">
              <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-12">
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Performance Analytics</h3>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">7-Day comparative operational data</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={380}>
                  <AreaChart data={weeklyOperationsData}>
                    <defs>
                      <linearGradient id="colorSch" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorComp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} />
                    <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 50px -10px rgba(0,0,0,0.1)' }} />
                    <Area type="monotone" dataKey="scheduled" stroke="#3B82F6" strokeWidth={4} fillOpacity={1} fill="url(#colorSch)" name="Volume" />
                    <Area type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={4} fillOpacity={1} fill="url(#colorComp)" name="Throughput" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100">
                  <h3 className="text-lg font-black text-slate-900 tracking-tight mb-8">Asset Utilization</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={otUtilizationData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} />
                      <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="OT-1" stroke="#3B82F6" strokeWidth={4} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 8 }} />
                      <Line type="monotone" dataKey="OT-2" stroke="#10B981" strokeWidth={4} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100 flex flex-col items-center justify-center">
                  <div className="p-8 bg-teal-50 rounded-full mb-6">
                    <TrendingUp className="w-12 h-12 text-teal-600" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">Predictive Insights</h3>
                  <p className="text-slate-400 text-center text-sm font-bold uppercase tracking-widest mt-4 max-w-xs">
                    System predicts 22% increase in orthopedic cases next week.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    className="mt-8 px-8 py-4 bg-teal-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-teal-100"
                  >
                    View Full Forecast
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
